/**
 * The one report layout — CLAUDE.md §4.2. Every report renders through this, and a report needing
 * something it cannot express gets a new optional slot here rather than a layout of its own.
 * Loading and error states live here too (§4.7), so no report writes its own.
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDatabase } from '@fortawesome/free-solid-svg-icons';
import { format, isValid, parseISO } from 'date-fns';

import { KioskToggle } from '@/components/report/KioskToggle';
import { ReportError } from '@/components/report/ReportError';
import { TruncationNotice } from '@/components/report/TruncationNotice';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useClock } from '@/hooks/useKioskMode';
import { useI18n } from '@/lib/i18n';

export interface ReportPageProps {
  /** The report's name as factory users say it (§4.10). */
  title: string;
  /** One line on what the report answers. Optional, but a report nobody can explain is a smell. */
  description?: React.ReactNode;
  /** Headline figures for the current filters. Where `<SummaryBar>` lands once §4.6 is settled. */
  summary?: React.ReactNode;
  /** `<FilterBar schema={...} />`. Stays interactive while the query runs and while it fails. */
  filters?: React.ReactNode;
  /** Shared view/tab selector for a governed multi-view report. */
  views?: React.ReactNode;
  /** Shared actions that apply to the active report view. */
  toolbar?: React.ReactNode;
  /**
   * The report's chart, inside a card of its own. Omit it — or pass null for a view that has no
   * chart — and no card is drawn; a card wrapping nothing is an empty bar above the table.
   */
  chart?: React.ReactNode;
  table: React.ReactNode;
  isLoading?: boolean;
  /** From `useReportQuery`. Rendered as a problem detail, never as a stack trace (§5.8). */
  error?: Error | null;
  /** When the response was produced. Not freshness — see below. */
  generatedAt?: string | null;
  /**
   * The latest source refresh the rows represent (§5.7) — the one a reader needs, and never
   * interchangeable with `generatedAt`. May legitimately be null.
   */
  dataAsOf?: string | null;
  /**
   * The server's row cap cut this result short (§5.7). Said out loud above the table, because
   * everything else on screen looks like a whole answer — and §4.3's rule that a capped result
   * shows no total only works if the page knows it is capped.
   */
  truncated?: boolean;
  /**
   * Render for a wall-mounted screen — see `useKioskMode`. A new optional slot on the one layout,
   * not a second layout (§4.2).
   */
  kiosk?: boolean;
  /**
   * What the kiosk bar names instead of `title` — the floor rather than the report. Falls back to
   * `title`; ignored off-kiosk.
   */
  kioskHeading?: React.ReactNode;
}

const TIMESTAMP = 'd MMM yyyy, HH:mm';

function readable(iso: string | null | undefined): string | null {
  if (iso === null || iso === undefined) {
    return null;
  }
  const parsed = parseISO(iso);
  return isValid(parsed) ? format(parsed, TIMESTAMP) : null;
}

/**
 * Source freshness and response time, kept visibly separate — collapsing them is the mistake §5.7
 * exists to prevent. A missing `dataAsOf` renders nothing rather than a "not reported" note.
 */
function Freshness({
  generatedAt,
  dataAsOf,
  isLoading,
}: {
  generatedAt: string | null | undefined;
  dataAsOf: string | null | undefined;
  isLoading: boolean;
}) {
  const { text } = useI18n();
  const source = readable(dataAsOf);
  const generated = readable(generatedAt);

  if (isLoading && source === null && generated === null) {
    return <Skeleton className="h-8 w-56" />;
  }

  return (
    <div className="flex flex-col gap-0.5 text-xs text-muted-foreground sm:items-end">
      {source === null ? null : (
        <span className="flex items-center gap-1.5">
          <FontAwesomeIcon icon={faDatabase} className="size-3" aria-hidden />
          {text('Data as of')}{' '}
          <time dateTime={dataAsOf ?? undefined} className="font-medium text-foreground">
            {source}
          </time>
        </span>
      )}
      {generated === null ? null : (
        <span>
          {text('This view ran at')} <time dateTime={generatedAt ?? undefined}>{generated}</time>
        </span>
      )}
    </div>
  );
}

/**
 * The kiosk header. The live dot answers "is this tab stuck?", which a wall screen raises and a
 * desk never does. It is a liveness signal, not a freshness one — `dataAsOf` is that (§5.7).
 */
function KioskBar({
  heading,
  dataAsOf,
}: {
  heading: React.ReactNode;
  dataAsOf: string | null | undefined;
}) {
  const clock = useClock();
  const { text } = useI18n();
  const source = readable(dataAsOf);

  return (
    <header className="flex flex-none flex-wrap items-center gap-x-6 gap-y-1 border-b border-border px-6 py-3">
      <h1 className="text-2xl font-semibold tracking-tight">{heading}</h1>
      <div className="ms-auto flex items-center gap-5 text-base text-muted-foreground">
        {source === null ? null : (
          <span className="flex items-center gap-2">
            <FontAwesomeIcon icon={faDatabase} className="size-3.5" aria-hidden />
            {text('Data as of')}{' '}
            <time dateTime={dataAsOf ?? undefined} className="font-medium text-foreground">
              {source}
            </time>
          </span>
        )}
        <span className="flex items-center gap-2">
          {/* motion-safe, so prefers-reduced-motion gets a steady dot rather than none (§4.9). */}
          <span
            className="size-2.5 rounded-full bg-status-ok motion-safe:animate-pulse"
            aria-hidden
          />
          <span className="sr-only">Live. </span>
          <time className="tabular-nums">{clock}</time>
        </span>
        <KioskToggle className="text-muted-foreground" />
      </div>
    </header>
  );
}

export function ReportPage({
  title,
  description,
  summary,
  filters,
  views,
  toolbar,
  chart,
  table,
  isLoading = false,
  error = null,
  generatedAt,
  dataAsOf,
  truncated = false,
  kiosk = false,
  kioskHeading,
}: ReportPageProps) {
  const { text } = useI18n();
  const failed = error !== null && error !== undefined;

  if (kiosk) {
    // Filters, views and toolbar are navigation for someone who can click; on a wall screen they
    // are noise, and the URL already fixes what is displayed.
    //
    // Outer padding is a TV overscan safe gutter: many wall screens crop ~2–5% of each edge of the
    // frame, so content that looks fine on a 1:1 monitor disappears on the set. 3vmin (~32px on
    // 1080p, scales with resolution) keeps the board inside that crop; max() floors a tiny
    // viewport. The background still fills the viewport via the shell — only the content insets.
    // Inner px-6 gutters stay for content rhythm; they are not a substitute for this outer zone.
    return (
      <div
        className="flex h-dvh flex-col overflow-hidden p-[max(1.5rem,3vmin)]"
        aria-busy={isLoading}
      >
        <KioskBar heading={kioskHeading ?? text(title)} dataAsOf={dataAsOf} />
        {summary}
        {failed ? (
          <div className="p-6">
            <ReportError error={error} />
          </div>
        ) : (
          <>
            {chart == null ? null : <div className="flex-none px-6 py-3">{chart}</div>}
            {truncated && !isLoading ? (
              <div className="flex-none px-6">
                <TruncationNotice />
              </div>
            ) : null}
            {/* A flex column, not just a scroll box: a board sizes its rows to a definite height.
                min-h-0 keeps it off the kiosk bar; overflow-auto beats clipping rows off a panel. */}
            <div className="flex min-h-0 flex-1 flex-col overflow-auto px-6 pb-2">{table}</div>
          </>
        )}
      </div>
    );
  }

  return (
    // gap-1.5 sets the rhythm between every slot on every report; below it the filter bar and the
    // toolbar stop reading as two separate bands of controls.
    <div className="flex w-full flex-col gap-1.5" aria-busy={isLoading}>
      <header className="flex flex-wrap items-start justify-between gap-x-6 gap-y-1">
        <div className="min-w-0 flex-1 flex flex-col gap-0.5">
          <h1 className="text-lg font-semibold tracking-tight text-balance">{text(title)}</h1>
          {description === undefined ? null : (
            // No max-w-prose — that capped the line at ~65ch and forced a wrap on a short sentence.
            // Wrapping stays ON, though: `whitespace-nowrap` here made any description longer than
            // the viewport scroll the WHOLE PAGE sideways, which §4.9 does not allow. `text-pretty`
            // is what keeps a short sentence from breaking badly.
            <p className="max-w-none text-xs text-pretty text-muted-foreground">{description}</p>
          )}
        </div>
        {/* Beside the title, not under the table: a number nobody can date is one nobody can act on. */}
        <Freshness generatedAt={generatedAt} dataAsOf={dataAsOf} isLoading={isLoading} />
      </header>

      {/* Filters first, and outside the error branch: when a report fails, adjusting a filter is
          the most likely fix and must not require going back. */}
      {filters}

      {summary}

      {views}

      {toolbar}

      {failed ? (
        <ReportError error={error} />
      ) : (
        <>
          {/* Nullish, not undefined: a view that turns its chart off returns null from a ternary,
              and an empty card is what the reader sees if that counts as "has a chart". */}
          {chart == null ? null : (
            <Card>
              <CardContent>
                {isLoading ? <Skeleton className="h-64 w-full" /> : chart}
              </CardContent>
            </Card>
          )}
          {/* Above the table: a caveat under a thousand rows is one nobody reaches. */}
          {truncated && !isLoading ? <TruncationNotice /> : null}
          {table}
        </>
      )}
    </div>
  );
}
